import React, { useState } from 'react'
import {
  getBbl,
  getBblDisplayNames,
  isForkjopActive,
} from 'shared/store/bbl/selectors'
import { useAppSelector } from 'shared/store/store'
import { getActiveClient } from 'shared/store/clients/selectors'
import { Link } from 'react-router-dom'
import {
  Button,
  getDomainFromUrl,
  getFormattedOrganizationNumber,
  getFormattedPhone,
  getUrlWithProtocol,
  IconMail,
  IconPhone,
  Image,
  Text,
} from '@bbl-digital/snorre-v2'
import { getLogoInverted } from 'shared/utils/theme'
import ContactModal from './ContactModal'

const FooterLayout: React.FC = () => {
  const { data } = useAppSelector(getBbl)
  const activeClient = useAppSelector(getActiveClient)
  const [contactOpen, setContactOpen] = useState(false)
  const { portal } = useAppSelector(getBblDisplayNames)
  const hasPreemption = useAppSelector(isForkjopActive)

  const privacyUrl = data?.settings?.customPrivacyStatementUrl
    ? getUrlWithProtocol(data.settings.customPrivacyStatementUrl)
    : '/privacy'

  const showGeneralQuestions =
    data?.settings?.phoneNumberForGeneralQuestions ||
    data?.settings?.emailForGeneralQuestions

  const showPortalQuestions =
    data?.settings?.phoneNumberForPortalQuestions ||
    data?.settings?.emailForPortalQuestions

  const businessAddress = `${activeClient?.businessZip} ${activeClient?.businessCity}`

  return (
    <>
      {data && (
        <div className="max-w-full bg-surface-portalen-footer">
          <footer className="flex flex-col justify-between w-auto px-4 pb-3 pt-6">
            <div className="flex flex-col md:flex-row gap-3 pb-4 lg:pb-0 lg:gap-1">
              <div className="flex max-h-fit">
                <Image
                  src={getLogoInverted(true)}
                  alt={`${data?.shortName || portal} logo`}
                  height="40px"
                  className="max-w-[200px]"
                />
              </div>

              <div className="flex-1 min-w-0 mb-2 lg:ml-[225px] pb-2">
                <div
                  className="
        grid gap-2
        grid-cols-1
        md:[grid-template-columns:repeat(2,minmax(0,250px))]
        xl:[grid-template-columns:repeat(auto-fit,minmax(0,1fr))]
      "
                >
                  <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                    <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                      Nyttige lenker
                    </Text>
                    {hasPreemption && (
                      <a
                        href={`${window.config.urls.forkjopUrl}${window.config.bblCode}`}
                        target="_blank"
                        className="text-surface-portalen-footer-text underline"
                        data-tracking-name="Footer"
                        data-tracking-event="Footer click: Forkjøpsrett"
                      >
                        Boliger på forkjøpsrett
                      </a>
                    )}
                    {data.settings.bblUrl && (
                      <a
                        href={`${getUrlWithProtocol(data.settings.bblUrl)}`}
                        target="_blank"
                        className="text-surface-portalen-footer-text underline"
                        data-tracking-name="Footer"
                        data-tracking-event="Footer click: BBL url"
                      >
                        {getDomainFromUrl(data.settings.bblUrl)}
                      </a>
                    )}
                  </div>
                  {activeClient && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        {activeClient?.clientName}
                      </Text>
                      {activeClient.companyNumber && (
                        <Text className="text-surface-portalen-footer-text">
                          {`Org.nr ${getFormattedOrganizationNumber(
                            activeClient.companyNumber
                          )}`}
                        </Text>
                      )}
                      {activeClient.businessAddress &&
                        data.settings.businessAddressPost && (
                          <>
                            <Text className="text-surface-portalen-footer-text">
                              {activeClient.businessAddress}
                            </Text>
                            <Text className="text-surface-portalen-footer-text">
                              {businessAddress}
                            </Text>
                          </>
                        )}
                      {(activeClient.invoiceAddress ||
                        activeClient.businessAddress) &&
                        data.settings.invoiceAddressPost && (
                          <Button
                            variant="nostyle"
                            className="text-surface-portalen-footer-text underline self-start"
                            onClick={() => setContactOpen(true)}
                            trackingName="Footer"
                            trackingEvent="Footer click: Fakturaadresse modal open"
                          >
                            Fakturaadresse
                          </Button>
                        )}
                    </div>
                  )}
                  {showGeneralQuestions && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        Generelle spørsmål
                      </Text>
                      {data.settings.phoneNumberForGeneralQuestions && (
                        <a
                          href={`tel:${data.settings.phoneNumberForGeneralQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Generelle spørsmål telefon"
                        >
                          <IconPhone colorVariant="currentColor" decorative />
                          {getFormattedPhone(
                            data.settings.phoneNumberForGeneralQuestions
                          )}
                        </a>
                      )}
                      {data.settings.emailForGeneralQuestions && (
                        <a
                          href={`mailto:${data.settings.emailForGeneralQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Generelle spørsmål email"
                        >
                          <IconMail className="size-2 text-surface-portalen-footer-text" />
                          {data.settings.emailForGeneralQuestions}
                        </a>
                      )}
                    </div>
                  )}
                  {showPortalQuestions && (
                    <div className="flex flex-1 flex-col gap-0 lg:!gap-1 max-h-fit">
                      <Text className="text-surface-portalen-footer-text text-lg font-bold pb-1">
                        Spørsmål om {portal}
                      </Text>
                      {data.settings.phoneNumberForPortalQuestions && (
                        <a
                          href={`tel:${data.settings.phoneNumberForPortalQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Spørsmål om portalen telefon"
                        >
                          <IconPhone colorVariant="currentColor" decorative />
                          {getFormattedPhone(
                            data.settings.phoneNumberForPortalQuestions
                          )}
                        </a>
                      )}
                      {data.settings.emailForPortalQuestions && (
                        <a
                          href={`mailto:${data.settings.emailForPortalQuestions}`}
                          target="_blank"
                          className="text-surface-portalen-footer-text underline flex items-center gap-1"
                          data-tracking-name="Footer"
                          data-tracking-event="Footer click: Spørsmål om portalen email"
                        >
                          <IconMail className="size-2" />
                          {data.settings.emailForPortalQuestions}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-between border-t border-surface-portalen-aside-icon pt-3 flex-col md:!flex-row">
              <Text className="text-surface-portalen-footer-text text-sm">
                © BBL Pivotal. Versjon 2.0.1
              </Text>
              {privacyUrl ? (
                <a
                  href={privacyUrl}
                  target="_blank"
                  data-tracking-name="Footer"
                  data-tracking-event="Footer click: Privacy"
                  className="text-surface-portalen-footer-text underline"
                >
                  Personvern og informasjonskapsler
                </a>
              ) : (
                <Link
                  to={'/privacy'}
                  data-tracking-name="Footer"
                  data-tracking-event="Footer click: Privacy"
                  className="text-surface-portalen-footer-text underline"
                >
                  Personvern og informasjonskapsler
                </Link>
              )}
            </div>
          </footer>
        </div>
      )}

      {contactOpen && (
        <ContactModal
          open={contactOpen}
          onClose={() => setContactOpen(false)}
        />
      )}
    </>
  )
}

export default FooterLayout
